import React, { useState, useEffect, useCallback, useMemo } from 'react';
import uniqBy from 'lodash.uniqby';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import { hideVisually } from 'polished';
import Fuse from 'fuse.js';
import CircularProgress from '../../../CircularProgress';
import { ENTER, DOWN_ARROW } from '../../../../../utils/keyCodes';
import { stringIsContained } from '../../../../../utils/search';

import {
  SpaceSelectorContainer,
  SpaceAvatar,
  ArrowDropdownIcon,
  ArrowForwardIcon,
  SpaceTitle,
  SpaceSelectorTarget,
  Text,
  List,
  Divider,
  SpacesWithStaffButton,
} from './SpaceSelector.styled';
import CurrentSpace from './CurrentSpace';
import StaffSpaces from './StaffSpaces.container';
import SpaceProptypes from './Space.proptype';
import SpaceFilter from './SpaceFilter';
import SpaceSelectorItem from './SpaceSelectorItem.container';
import SideDialog from '../../../common/SideDialog';

const defaultSort = 'ascending';
const renderSpacesFilterThreshold = 7;

const SpaceSelector = ({
  currentSpace,
  isLoading,
  userRoles,
  spaces,
  onSpaceImageChange,
  onSpaceSelect,
  isAdmin,
  tooltipPosition,
}) => {
  const [showArchived, setShowArchived] = useState(false);
  const toggleArchivedVisible = () => setShowArchived(!showArchived);

  const [spaceSelectorOpened, setSpaceSelectorOpened] = useState(false);
  const toggleSpaceSelectorOpened = () =>
    setSpaceSelectorOpened(!spaceSelectorOpened);

  const [fuse, setFuse] = useState(null);

  const [filter, setFilter] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');
  const [sortedBy, setSortedBy] = useState(defaultSort);

  const [filteredSpaces, setFilteredSpaces] = useState([]);
  const [dividerVisible, setDividerVisible] = useState(false);

  const [showStaffSpaces, setshowStaffSpaces] = useState(false);

  useEffect(() => {
    setFilteredSpaces([...spaces]);
  }, [spaces]);

  useEffect(() => {
    setFuse(
      new Fuse(spaces, {
        threshold: 0.3,
        location: 0,
        distance: 1000,
        keys: ['name'],
        shouldSort: true,
      })
    );
  }, [spaces]);

  const sortOptions = {
    ascending: {
      comparator: (a, b) => a.name.localeCompare(b.name),
      description: 'Name: A to Z',
      setSortOption: () => setSortedBy('ascending'),
    },
    descending: {
      comparator: (a, b) => b.name.localeCompare(a.name),
      description: 'Name: Z to A',
      setSortOption: () => setSortedBy('descending'),
    },
  };

  const searchSpaces = useCallback(
    (query) => {
      if (query) {
        setFilteredSpaces(
          uniqBy(
            [
              ...spaces.reduce(
                (matches, space) =>
                  stringIsContained({ parent: space.name, child: query })
                    ? [...matches, space]
                    : matches,
                []
              ),
              ...fuse.search(query),
            ],
            ({ id }) => id
          )
        );
        setDebouncedFilter(query);
      } else {
        setFilteredSpaces(spaces);
        setDebouncedFilter('');
      }
    },
    [spaces]
  );

  const searchSpacesWithDebounce = useCallback(debounce(searchSpaces, 200), [
    searchSpaces,
  ]);

  useEffect(() => {
    if (!filter) {
      // Reset spaces immediately if clearing the input
      searchSpaces(filter);
    } else {
      searchSpacesWithDebounce(filter);
    }
  }, [filter]);

  const onFilterChange = (query) => {
    setFilter(query);
  };

  const onArchivedToggleChange = () => {
    toggleArchivedVisible();
  };

  const renderSpaceAvatar = () =>
    isLoading ? (
      <CircularProgress size="32px" />
    ) : (
      <SpaceAvatar
        name={currentSpace.name}
        src={currentSpace.avatar}
        size="medium"
      />
    );

  const closeSpaceSelector = () => {
    setSpaceSelectorOpened(false);
    setFilter('');
    setFilteredSpaces(spaces);
    setShowArchived(false);
    setSortedBy(defaultSort);
    setDividerVisible(false);
  };

  const spacesToShow = useMemo(() => {
    if (spaces.length <= renderSpacesFilterThreshold) {
      return [
        ...spaces.filter(({ archived }) => !archived),
        ...spaces.filter(({ archived }) => archived),
      ];
    }

    let shownSpaces = [...filteredSpaces];

    if (!showArchived) {
      shownSpaces = shownSpaces.filter(({ archived }) => !archived);
    }

    if (filter) {
      return shownSpaces;
    }

    return shownSpaces.sort(sortOptions[sortedBy].comparator);
  }, [spaces, showArchived, debouncedFilter, sortedBy]);

  const handleClickSpace = useCallback(
    (id) => {
      if (spaceSelectorOpened) {
        closeSpaceSelector();
        onSpaceSelect(id);
      }
    },
    [spaceSelectorOpened]
  );

  const spaceList = useMemo(() => {
    return (
      <List compact>
        {spacesToShow.map(({ id, archived }) => (
          <SpaceSelectorItem
            key={id}
            spaceId={id}
            onClick={() => handleClickSpace(id)}
            archived={archived}
          />
        ))}
      </List>
    );
  }, [spacesToShow, handleClickSpace]);

  const onListItemsScroll = (e) => {
    const {
      target: { scrollTop },
    } = e;
    if (scrollTop === 0) {
      setDividerVisible(false);
    } else {
      setDividerVisible(true);
    }
  };

  const renderSpaceSelector = () => (
    <>
      <CurrentSpace
        space={currentSpace}
        isLoading={isLoading}
        onAvatarClick={onSpaceImageChange}
        isAdmin={isAdmin}
        tooltipPosition={tooltipPosition}
        avatarUploaderId="space-avatar-uploader"
      />
      <Text>Switch to another space</Text>
      {spaces.length > renderSpacesFilterThreshold && (
        <>
          <SpaceFilter
            filter={filter}
            onArchivedToggleChange={onArchivedToggleChange}
            currentSortOption={sortOptions[sortedBy].description}
            sortOptions={sortOptions}
            onFilterChange={onFilterChange}
            hasArchivedSpaces={spaces.some(({ archived }) => archived)}
          />
          {dividerVisible && <Divider />}
        </>
      )}
      {spaceList}
      <Divider />
      {userRoles.includes('staff') && (
        <SpacesWithStaffButton
          onClick={() => {
            setshowStaffSpaces(true);
          }}
        >
          Spaces with staff access
          <ArrowForwardIcon />
        </SpacesWithStaffButton>
      )}
    </>
  );

  const renderStaffSpaceSelector = () => (
    <StaffSpaces
      onBackClick={() => {
        setshowStaffSpaces(false);
      }}
      onSpaceSelect={(id) => handleClickSpace(id)}
    />
  );

  return (
    <SpaceSelectorContainer
      id="space-selector"
      data-testid="space-selector-title"
    >
      <SpaceSelectorTarget
        role="button"
        tabIndex="0"
        aria-expanded={spaceSelectorOpened}
        onClick={toggleSpaceSelectorOpened}
        onKeyDown={(e) => {
          if (e.keyCode === ENTER || e.keyCode === DOWN_ARROW) {
            toggleSpaceSelectorOpened();
          }
        }}
      >
        {renderSpaceAvatar()}
        <SpaceTitle title={currentSpace.name}>{currentSpace.name}</SpaceTitle>
        <ArrowDropdownIcon />
      </SpaceSelectorTarget>
      <SideDialog
        title="Spaces"
        open={spaceSelectorOpened}
        onClose={() => closeSpaceSelector()}
        width="488px"
        testAttribute="select-space"
        contentStyle={{
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          heigh: 'calc(100% - 67px)',
        }}
        onContentScroll={onListItemsScroll}
      >
        {showStaffSpaces ? renderStaffSpaceSelector() : renderSpaceSelector()}
      </SideDialog>
      <div
        id="current-space-name"
        tabIndex={-1}
        style={{ ...hideVisually() }}
        aria-live="polite"
      >
        {currentSpace.name}
      </div>
    </SpaceSelectorContainer>
  );
};

SpaceSelector.propTypes = {
  currentSpace: PropTypes.shape(SpaceProptypes).isRequired,
  userRoles: PropTypes.arrayOf(PropTypes.string),
  spaces: PropTypes.arrayOf(PropTypes.shape({})),
  onSpaceImageChange: PropTypes.func,
  onSpaceSelect: PropTypes.func,
  isLoading: PropTypes.bool,
  isAdmin: PropTypes.bool,
  tooltipPosition: PropTypes.string,
};

SpaceSelector.defaultProps = {
  spaces: [],
  userRoles: [],
  onSpaceImageChange: () => {},
  onSpaceSelect: () => {},
  isLoading: false,
  isAdmin: false,
  tooltipPosition: 'bottom',
};

export default React.memo(SpaceSelector);
